"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("@emotion/react");
/**
 * getTickIconSvg:
 * Generates a data URL for an SVG tick icon with a customizable fill color.
 *
 * @param {string} color - The color to be used for the fill of the SVG tick icon.
 * @returns {string} A base64-encoded data URL representing the tick icon SVG, which can be used as a background image in CSS.
 *
 * The function creates an SVG string representing a tick (checkmark) icon, with the fill color dynamically set based on the input `color` parameter.
 * The SVG is then converted to a base64-encoded data URL that can be used in CSS as a background image.
 */
var getTickIconSvg = function (color) {
    var tickIconSvg = "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"".concat(color, "\">\n      <path d=\"M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z\"/>\n    </svg>");
    var tickIconSvgBase64 = btoa(tickIconSvg);
    return "url(data:image/svg+xml;base64,".concat(tickIconSvgBase64, ")");
};
var makeTextStyling = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    var variant = _a.variant;
    var _o = (0, react_1.useTheme)(), mvf = _o.mvf, breakpoints = _o.breakpoints;
    var lineHeight = mvf.lineHeight, palette = mvf.palette, fontSize = mvf.fontSize, typography = mvf.typography, spacing = mvf.spacing;
    var weight = typography.weight;
    return (0, react_1.css)(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({ fontFamily: typography.fontFamily, letterSpacing: 'inherit' }, (variant === 'footer' && (_b = {
            fontSize: fontSize.tiny
        },
        _b[breakpoints.up('tabletSmall')] = {
            fontSize: fontSize.little,
        },
        _b.color = palette.text.secondaryTextColor,
        _b.textAlign = 'left',
        _b.lineHeight = lineHeight.small,
        _b))), (variant === 'header' && (_c = {
            fontSize: fontSize.small
        },
        _c[breakpoints.up('mobileMedium')] = {
            fontSize: fontSize.compact,
        },
        _c[breakpoints.up('tabletSmall')] = {
            fontSize: fontSize.medium,
        },
        _c[breakpoints.up('tabletMedium')] = {
            fontSize: fontSize.swoll,
        },
        _c[breakpoints.up('desktopSmall')] = {
            fontSize: fontSize.large,
        },
        _c[breakpoints.up('desktopMedium')] = {
            fontSize: fontSize.big,
        },
        _c.fontWeight = typography.weight.thick,
        _c))), (variant === 'summary' && (_d = {
            fontSize: fontSize.mini
        },
        _d[breakpoints.up('mobileMedium')] = {
            fontSize: fontSize.small,
        },
        _d[breakpoints.up('tabletSmall')] = {
            fontSize: fontSize.compact,
        },
        _d.lineHeight = lineHeight.small,
        _d.textAlign = 'left',
        _d.whiteSpace = 'pre-line',
        _d['& ol'] = {
            counterReset: 'li',
            listStyleType: 'none',
            paddingLeft: '0px',
            marginTop: '0px',
            '& li': {
                position: 'relative',
                padding: '20px 0 0 35px',
                '&::before': {
                    counterIncrement: 'li',
                    content: 'counter(li)',
                    border: "2px solid ".concat(palette.component.answerSelectedColor),
                    fontWeight: 'bold',
                    borderRadius: '50%',
                    position: 'absolute',
                    height: '20px',
                    width: '20px',
                    left: '0',
                    top: '18px',
                    textAlign: 'center',
                    color: palette.component.answerSelectedColor,
                },
            },
        },
        _d))), (variant === 'note' && (_e = {
            fontSize: fontSize.little
        },
        _e[breakpoints.up('tabletSmall')] = {
            fontSize: fontSize.mini,
        },
        _e[breakpoints.up('desktopSmall')] = {
            fontSize: fontSize.small,
        },
        _e.backgroundColor = palette.background.secondaryBackgroundColor,
        _e.color = palette.text.secondaryTextColor,
        _e.paddingLeft = spacing.medium,
        _e.paddingRight = spacing.medium,
        _e.paddingTop = spacing.tiny,
        _e.paddingBottom = spacing.tiny,
        _e.borderRadius = 4,
        _e))), (variant === 'question' && (_f = {
            fontSize: fontSize.medium
        },
        _f[breakpoints.up('mobileMedium')] = {
            fontSize: fontSize.hefty,
        },
        _f[breakpoints.up('tabletSmall')] = {
            fontSize: fontSize.swoll,
        },
        _f[breakpoints.up('tabletMedium')] = {
            fontSize: fontSize.large,
        },
        _f[breakpoints.up('desktopSmall')] = {
            fontSize: fontSize.big,
        },
        _f[breakpoints.up('desktopMedium')] = {
            fontSize: fontSize.huge,
        },
        _f.fontWeight = typography.weight.thick,
        _f))), (variant === 'listItem' && (_g = {
            wordBreak: 'break-word',
            fontSize: fontSize.mini
        },
        _g[breakpoints.up('mobileMedium')] = {
            fontSize: fontSize.small,
        },
        _g[breakpoints.up('tabletSmall')] = {
            fontSize: fontSize.compact,
        },
        _g.lineHeight = lineHeight.small,
        _g.padding = spacing.tiny,
        _g.fontWeight = typography.weight.thick,
        _g))), (variant === 'tileItem' && (_h = {
            fontSize: fontSize.mini,
            wordBreak: 'break-word'
        },
        _h[breakpoints.up('mobileMedium')] = {
            fontSize: fontSize.small,
        },
        _h[breakpoints.up('tabletSmall')] = {
            fontSize: fontSize.compact,
        },
        _h[breakpoints.up('desktopMedium')] = {
            fontSize: fontSize.medium,
        },
        _h.textAlign = 'center',
        _h.margin = spacing.tiny,
        _h.lineHeight = lineHeight.small,
        _h.wordWrap = 'break-word',
        _h.fontWeight = typography.weight.thick,
        _h))), (variant === 'text' && (_j = {
            fontSize: fontSize.mini
        },
        _j[breakpoints.up('mobileMedium')] = {
            fontSize: fontSize.small,
        },
        _j[breakpoints.up('tabletSmall')] = {
            fontSize: fontSize.compact,
        },
        _j.color = palette.text.secondaryTextColor,
        _j.lineHeight = lineHeight.small,
        _j))), (variant === 'contentWithList' && (_k = {
            fontSize: fontSize.little
        },
        _k[breakpoints.up('tabletSmall')] = {
            fontSize: fontSize.mini,
        },
        _k[breakpoints.up('desktopSmall')] = {
            fontSize: fontSize.small,
        },
        _k.backgroundColor = palette.background.secondaryBackgroundColor,
        _k.color = palette.text.secondaryTextColor,
        _k.paddingLeft = spacing.medium,
        _k.paddingRight = spacing.medium,
        _k.paddingTop = spacing.tiny,
        _k.paddingBottom = spacing.tiny,
        _k.borderRadius = 4,
        _k.textAlign = 'center',
        _k['& span, & ol, & ul'] = {
            backgroundColor: 'inherit',
            display: 'block',
        },
        _k['& ol, & ul'] = {
            paddingLeft: '0px',
            marginTop: '0px',
            listStylePosition: 'inside',
        },
        _k['& li'] = {
            margin: '0',
        },
        _k.margin = 0,
        _k.padding = 0,
        _k))), (variant === 'tickedList' && {
        color: palette.text.primaryTextColor,
        fontSize: fontSize.medium,
        fontWeight: weight.thick,
        '& li': (_l = {
                listStyleType: 'none',
                paddingLeft: 33,
                lineHeight: lineHeight.medium,
                fontWeight: weight.medium,
                fontSize: fontSize.compact
            },
            _l[breakpoints.up('tabletSmall')] = {
                fontSize: fontSize.medium,
                fontWeight: weight.thick,
            },
            _l.background = "".concat(getTickIconSvg(palette.component.answerSelectedColor), " no-repeat left 3px"),
            _l.backgroundSize = '20px 20px',
            _l),
        '& ul': (_m = {
                margin: '0 auto',
                width: 'fit-content'
            },
            _m[breakpoints.up('tabletSmall')] = {
                width: '100%',
            },
            _m.padding = 0,
            _m),
    })));
};
exports.default = makeTextStyling;
//# sourceMappingURL=makeTextStyling.js.map